import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { MediaObject, SupportEvent, SupportTicket, SupportTicketDetail } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { Button } from '@localagent/ui/components/button';
import { getSDK } from '../services/sdk-instance';
import { PageShell } from '../components/layout/PageShell';
import { usePublishViewSnapshot } from '../components/shogun/usePublishViewSnapshot';
import { useSupportLiveRefresh } from '../hooks/useSupportLiveRefresh';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export function SupportDetail({
  ticketId,
  fixture,
  operator,
  artifacts,
  live,
  userId,
}: {
  ticketId: string;
  fixture?: SupportTicketDetail;
  operator: boolean;
  artifacts?: MediaObject[];
  live: boolean;
  userId?: string;
}) {
  const queryClient = useQueryClient();
  const [detail, setDetail] = useState<SupportTicketDetail | undefined>(fixture);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [artifactId, setArtifactId] = useState('');
  const [media, setMedia] = useState<MediaObject[]>(artifacts ?? []);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [assignee, setAssignee] = useState('');

  const load = useCallback(async () => {
    if (!live) return;
    try {
      const got = operator
        ? await getSDK().support.adminGet(ticketId)
        : await getSDK().support.get(ticketId);
      setDetail(got);
      setTitle(got.ticket.title);
      setStatus(got.ticket.status);
      setPriority(got.ticket.priority);
      setAssignee(got.ticket.assignee_user_id ?? '');
      setError(null);
      if (operator) {
        await getSDK().support.adminMarkRead(got.ticket.id);
      } else {
        await getSDK().support.markRead(got.ticket.id);
      }
      await queryClient.invalidateQueries({ queryKey: ['support', 'unseen'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [live, operator, queryClient, ticketId]);

  useEffect(() => {
    if (fixture) {
      setDetail(fixture);
      setTitle(fixture.ticket.title);
      setStatus(fixture.ticket.status);
      setPriority(fixture.ticket.priority);
      setAssignee(fixture.ticket.assignee_user_id ?? '');
      return;
    }
    void load();
  }, [fixture, load]);

  useEffect(() => {
    if (artifacts !== undefined) {
      setMedia(artifacts);
      return;
    }
    if (!live) return;
    let cancelled = false;
    void getSDK()
      .media.list({ limit: 100, trashed: false })
      .then((page) => {
        if (!cancelled) setMedia(page.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [artifacts, live]);

  const onLiveEvent = useCallback(
    (event: SupportEvent) => {
      if (!detail) {
        void load();
        return;
      }
      if (event.ticket_id === detail.ticket.id) {
        void load();
      }
      void queryClient.invalidateQueries({ queryKey: ['support', 'unseen'] });
    },
    [detail, load, queryClient],
  );
  useSupportLiveRefresh(onLiveEvent);

  const snapshot = useMemo(() => {
    if (!detail) return null;
    return {
      route: `/support/${ticketId}`,
      context: {
        surface: 'support',
        primary_type: 'support_ticket',
        counts: [{ lane: 'thread', total: detail.comments.length }],
        visible: [
          {
            type: 'support_ticket',
            id: detail.ticket.id,
            label: detail.ticket.display_id,
            state: detail.ticket.status,
          },
        ],
      },
    };
  }, [detail, ticketId]);
  usePublishViewSnapshot(snapshot);

  const visibleComments = (detail?.comments ?? []).filter(
    (c) => operator || c.visibility !== 'internal',
  );

  async function submitComment() {
    if (!detail || body.trim() === '') return;
    try {
      if (operator) {
        await getSDK().support.adminComment(detail.ticket.id, {
          body: body.trim(),
          visibility: internal ? 'internal' : 'public',
        });
      } else {
        await getSDK().support.comment(detail.ticket.id, { body: body.trim() });
      }
      setBody('');
      setInternal(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function submitAttach() {
    if (!detail || artifactId === '') return;
    try {
      if (operator) {
        await getSDK().support.adminAttach(detail.ticket.id, { artifact_id: artifactId });
      } else {
        await getSDK().support.attach(detail.ticket.id, { artifact_id: artifactId });
      }
      setArtifactId('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function submitUpdate() {
    if (!detail || !operator) return;
    try {
      await getSDK().support.adminUpdate(detail.ticket.id, {
        title: title.trim() === '' ? undefined : title.trim(),
        status: status === '' ? undefined : (status as SupportTicket['status']),
        priority: priority === '' ? undefined : (priority as SupportTicket['priority']),
        assignee_user_id: assignee === '' ? null : assignee,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <PageShell
      title={detail ? `${detail.ticket.display_id} · ${detail.ticket.title}` : 'Support'}
      description={detail ? detail.ticket.status : undefined}
      headerActions={
        <Link
          to="/support"
          className="inline-flex h-9 items-center rounded-md bg-secondary px-3 text-sm text-secondary-foreground hover:bg-secondary/80"
        >
          Inbox
        </Link>
      }
    >
      <div data-testid="support-detail">
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {!detail ? (
          <LoadingState label="Loading ticket…" />
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge data-testid="support-detail-status">{detail.ticket.status}</Badge>
              <Badge>{detail.ticket.priority}</Badge>
              <Badge>{detail.ticket.audience}</Badge>
            </div>
            <p className="whitespace-pre-wrap text-sm">{detail.ticket.description}</p>
            {detail.ticket.session_id ? (
              detail.ticket.session_gone ? (
                <p data-testid="support-session-gone" className="text-sm text-muted-foreground">
                  Intake session is gone.
                </p>
              ) : (
                <Link
                  data-testid="support-session-link"
                  className="text-sm underline"
                  to={`/chat/${detail.ticket.session_id}`}
                >
                  Open intake session
                </Link>
              )
            ) : null}

            {operator ? (
              <div data-testid="support-operator-controls" className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs">
                  Title
                  <input
                    data-testid="support-operator-title"
                    className="h-9 w-64 rounded-md bg-muted px-2 text-sm"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  Status
                  <select
                    data-testid="support-operator-status"
                    className="h-9 rounded-md bg-muted px-2 text-sm"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="open">open</option>
                    <option value="pending_response">pending_response</option>
                    <option value="pending">pending</option>
                    <option value="resolve">resolve</option>
                    <option value="closed">closed</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  Priority
                  <select
                    data-testid="support-operator-priority"
                    className="h-9 rounded-md bg-muted px-2 text-sm"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="urgent">urgent</option>
                    <option value="high">high</option>
                    <option value="medium">medium</option>
                    <option value="low">low</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  Assignee
                  <input
                    data-testid="support-operator-assignee"
                    className="h-9 rounded-md bg-muted px-2 text-sm"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    placeholder={userId}
                  />
                </label>
                <Button type="button" data-testid="support-operator-save" onClick={() => void submitUpdate()}>
                  Save
                </Button>
              </div>
            ) : null}

            <section>
              <h2 className="mb-2 text-sm font-semibold">Thread</h2>
              <ol data-testid="support-comment-list" className="space-y-3">
                {visibleComments.map((comment) => (
                  <li
                    key={comment.id}
                    data-testid={`support-comment-${comment.visibility}`}
                    className="rounded-md bg-card px-3 py-2 text-sm"
                  >
                    <div className="mb-1 text-xs text-muted-foreground">
                      {comment.visibility}
                      {comment.visibility === 'internal' ? ' · staff only' : ''}
                    </div>
                    <p className="whitespace-pre-wrap">{comment.body}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-3 space-y-2">
                <textarea
                  data-testid="support-reply-input"
                  className="min-h-24 w-full rounded-md bg-muted px-2 py-1 text-sm"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                {operator ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid="support-internal-note"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                    />
                    Internal note
                  </label>
                ) : null}
                <Button type="button" data-testid="support-reply-submit" onClick={() => void submitComment()}>
                  Reply
                </Button>
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold">Attachments</h2>
              <ul data-testid="support-attachment-list" className="space-y-1 text-sm">
                {detail.attachments.map((att) => (
                  <li key={att.id} data-testid={`support-attachment-${att.id}`}>
                    {att.artifact_label ?? att.artifact_id}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  data-testid="support-attach-select"
                  className="h-9 rounded-md bg-muted px-2 text-sm"
                  value={artifactId}
                  onChange={(e) => setArtifactId(e.target.value)}
                >
                  <option value="">Choose an artifact</option>
                  {media.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <Button type="button" data-testid="support-attach-submit" onClick={() => void submitAttach()}>
                  Attach
                </Button>
              </div>
            </section>
          </div>
        )}
      </div>
    </PageShell>
  );
}
